// src/components/laboratory/tabs/UsersTab.jsx - Fixed version
import React, { useState } from 'react';
import { Plus, Users, UserPlus, Mail, Phone, Search, Trash2, Crown, User, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';

const RESEND_USER_ACTIVATION = gql`
  mutation ResendUserActivationEmail($userId: ID!) {
    resendUserActivationEmail(userId: $userId)
  }
`;

const SEND_PASSWORD_RESET = gql`
  mutation SendUserPasswordResetEmail($userId: ID!) {
    sendUserPasswordResetEmail(userId: $userId)
  }
`;

const UserActionButton = ({ userId, isActive }) => {
  const [sent, setSent] = useState(false);
  const [resend, { loading: resending }] = useMutation(RESEND_USER_ACTIVATION, {
    onCompleted: () => { setSent(true); toast.success("Email d'activation envoyé"); setTimeout(() => setSent(false), 4000); },
    onError: (err) => toast.error(err.message || 'Erreur lors du renvoi'),
  });
  const [sendReset, { loading: resetting }] = useMutation(SEND_PASSWORD_RESET, {
    onCompleted: () => { setSent(true); toast.success('Email de réinitialisation envoyé'); setTimeout(() => setSent(false), 4000); },
    onError: (err) => toast.error(err.message || 'Erreur lors de l\'envoi'),
  });

  const loading = resending || resetting;
  const label = isActive ? 'Réinitialiser mdp' : 'Renvoyer activation';

  const handleClick = () => {
    if (isActive) {
      sendReset({ variables: { userId } });
    } else {
      resend({ variables: { userId } });
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading || sent}
      title={label}
      className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-60 transition-colors"
    >
      <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
      {sent ? 'Envoyé !' : label}
    </button>
  );
};

const UsersTab = ({
  laboratory,
  onAddUser,
  onRemoveUser
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Get owner and staff from laboratory data
  const owner = laboratory.owner;
  const staffUsers = laboratory.staffUsers || [];
  const allUsers = laboratory.users || [];

  // Filter staff users based on search term
  const filteredStaff = staffUsers.filter(user => {
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
    return (
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fullName.includes(searchTerm.toLowerCase())
    );
  });

  const totalUsers = allUsers.length;
  const totalStaff = staffUsers.length;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            Utilisateurs ({totalUsers})
          </h3>
          <p className="text-sm text-gray-500">
            {owner ? '1 propriétaire' : '0 propriétaire'} + {totalStaff} collaborateur{totalStaff !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={onAddUser}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500"
        >
          <UserPlus className="-ml-0.5 mr-1 h-4 w-4" />
          Ajouter un collaborateur
        </button>
      </div>

      {/* Search bar - for staff only */}
      {totalStaff > 0 && (
        <div className="relative w-full">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
            placeholder="Rechercher un collaborateur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      {/* Owner section */}
      <div className="bg-white shadow sm:rounded-lg mb-4">
        <div className="px-4 py-5 sm:px-6 bg-gray-50">
          <div className="flex items-center">
            <Crown className="h-5 w-5 text-yellow-500 mr-2" />
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Propriétaire principal
            </h3>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Le propriétaire principal est administrateur du laboratoire
          </p>
        </div>
        <div className="border-t border-gray-200">
          {owner ? (
            <div className="px-4 py-5 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                        <Crown className="h-5 w-5 text-green-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-md font-medium text-gray-900">
                        {owner.firstName} {owner.lastName}
                      </h4>
                      <div className="mt-1 flex items-center">
                        <Mail className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-500">{owner.email}</p>
                      </div>
                      {owner.tel && (
                        <div className="mt-1 flex items-center">
                          <Phone className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                          <p className="text-sm text-gray-500">{owner.tel}</p>
                        </div>
                      )}
                      {owner.lastLogin && (
                        <p className="mt-1 text-xs text-gray-400">
                          Dernière connexion: {format(new Date(owner.lastLogin), 'dd/MM/yyyy HH:mm', { locale: fr })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Propriétaire
                  </span>
                  {owner.isActive ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Actif
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Inactif
                    </span>
                  )}
                  <UserActionButton userId={owner.id} isActive={owner.isActive} />
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 py-5 sm:px-6 text-center">
              <div className="flex flex-col items-center">
                <Crown className="h-12 w-12 text-gray-300 mb-2" />
                <h4 className="text-sm font-medium text-gray-900 mb-1">Aucun propriétaire</h4>
                <p className="text-sm text-gray-500">
                  Aucun propriétaire principal n'est défini pour ce laboratoire
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Staff/Collaborators section */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 bg-gray-50">
          <div className="flex items-center">
            <Users className="h-5 w-5 text-blue-500 mr-2" />
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Collaborateurs ({totalStaff})
            </h3>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Utilisateurs collaborateurs ayant accès au laboratoire
          </p>
        </div>
        <div className="border-t border-gray-200">
          {filteredStaff.length === 0 ? (
            <div className="text-center py-8">
              <Users size={40} className="mx-auto text-gray-400 mb-2" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {searchTerm ? "Aucun collaborateur trouvé" : "Aucun collaborateur"}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 
                  "Aucun collaborateur ne correspond à votre recherche." : 
                  "Ce laboratoire n'a pas encore de collaborateurs."}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-3 text-sm text-green-600 hover:text-green-500"
                >
                  Réinitialiser la recherche
                </button>
              )}
              {!searchTerm && (
                <button
                  onClick={onAddUser}
                  className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <UserPlus className="-ml-0.5 mr-2 h-4 w-4" />
                  Ajouter le premier collaborateur
                </button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {filteredStaff.map((user) => (
                <li key={user.id}>
                  <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <h4 className="text-md font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </h4>
                          <div className="mt-1 flex items-center">
                            <Mail className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                          {user.tel && (
                            <div className="mt-1 flex items-center">
                              <Phone className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                              <p className="text-sm text-gray-500">{user.tel}</p>
                            </div>
                          )}
                          {user.lastLogin && (
                            <p className="mt-1 text-xs text-gray-400">
                              Dernière connexion: {format(new Date(user.lastLogin), 'dd/MM/yyyy HH:mm', { locale: fr })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Collaborateur
                          </span>
                          {user.isActive ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Actif
                            </span>
                          ) : (
                            <>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                En attente
                              </span>
                              <UserActionButton userId={user.id} isActive={false} />
                            </>
                          )}
                        </div>
                        <button
                          onClick={() => onRemoveUser(user)}
                          className="text-red-600 hover:text-red-900 transition-colors duration-200"
                          title="Retirer du laboratoire"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersTab;